<template>
  <div v-if="isVisible" class="modal-container modal-container-small" :class="{ 'modal-minimized': isMinimized }"
    ref="modal" :style="{
      top: modalState.top,
      left: modalState.left,
      width: modalState.width,
      height: modalState.height
    }">
    <div class="modal-header" @mousedown="startDrag" @touchstart="startDrag">
      <h5>{{ title }}</h5>
      <div>
        <button v-if="showHideButton" class="btn btn-sm btn-hide" @click="hiddeModal">-</button>
        <button class="btn btn-sm btn-close" @click="closeModal">×</button>
      </div>
    </div>
    <div class="modal-body" v-show="!isMinimized">
      <slot></slot>
    </div>
    <div class="modal-resizer" @mousedown="startResize" @touchstart="startResize" v-show="!isMinimized">
      <svg width="24" height="24" viewBox="0 0 24 24">
        <polyline points="6,18 18,18 18,6" style="fill: none; stroke: #888; stroke-width: 2" />
      </svg>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref, computed, watch, onMounted, watchEffect, onBeforeUnmount, onUnmounted } from 'vue'
import { useGlobalStore } from '@/stores/global'

const props = defineProps({
  modalId: { type: String, required: true },
  title: { type: String, default: 'Modal' },
  isVisible: { type: Boolean, required: true },
  showHideButton: { type: Boolean, default: true },
  top: { type: [String, Number], default: undefined },
  left: { type: [String, Number], default: undefined },
  width: { type: [String, Number], default: undefined },
  height: { type: [String, Number], default: undefined }
})

const emit = defineEmits(['close', 'minimize', 'restore'])
const globalStore = useGlobalStore()
const modal = ref<HTMLElement | null>(null)
const isMinimized = ref(false)
let isDragging = false
let isResizing = false
let dragStartX = 0
let dragStartY = 0
let modalStartX = 0
let modalStartY = 0
let modalStartWidth = 0
let modalStartHeight = 0

// Inicializa o actualiza el estado en el store
const existingState = globalStore.modalsState[props.modalId]
if (!existingState) {
  // Primera vez: usa los props por defecto
  globalStore.setModalState(props.modalId, {
    top:
      props.top !== undefined
        ? typeof props.top === 'number'
          ? props.top + 'px'
          : props.top
        : '50px',
    left:
      props.left !== undefined
        ? typeof props.left === 'number'
          ? props.left + 'px'
          : props.left
        : '50px',
    width:
      props.width !== undefined
        ? typeof props.width === 'number'
          ? props.width + 'px'
          : props.width
        : '600px',
    height:
      props.height !== undefined
        ? typeof props.height === 'number'
          ? props.height + 'px'
          : props.height
        : '400px'
  })
}
// Computed reactivo para el estado actual del modal
const modalState = computed(() => {
  // Siempre usa el store
  return globalStore.modalsState[props.modalId]
})

function closeModal() {
  // Guarda el estado actual antes de cerrar
  if (modal.value) {
    const rect = modal.value.getBoundingClientRect()
    globalStore.setModalState(props.modalId, {
      left: `${rect.left}px`,
      top: `${rect.top}px`,
      width: `${rect.width}px`,
      height: `${rect.height}px`
    })
  }
  emit('close')
}
// Expone el estado minimizado para que el sidebar pueda accederlo
defineExpose({
  isMinimized,
  restoreModal
})

function hiddeModal() {
  // Guarda el estado actual antes de minimizar
  if (modal.value) {
    const rect = modal.value.getBoundingClientRect()
    globalStore.setModalState(props.modalId, {
      left: `${rect.left}px`,
      top: `${rect.top}px`,
      width: `${rect.width}px`,
      height: `${rect.height}px`
    })
  }

  // Minimiza en lugar de cerrar
  isMinimized.value = true
  emit('minimize', props.modalId) // Pasa el modalId para que el padre lo identifique
}

function restoreModal() {
  console.log('Restaurando modal:', props.modalId)
  isMinimized.value = false
  emit('restore', props.modalId)
}

function getEventClientX(e: MouseEvent | TouchEvent) {
  return 'touches' in e ? e.touches[0].clientX : (e as MouseEvent).clientX
}
function getEventClientY(e: MouseEvent | TouchEvent) {
  return 'touches' in e ? e.touches[0].clientY : (e as MouseEvent).clientY
}
function startDrag(event: MouseEvent | TouchEvent) {
  isDragging = true
  dragStartX = getEventClientX(event)
  dragStartY = getEventClientY(event)
  const rect = modal.value!.getBoundingClientRect()
  modalStartX = rect.left
  modalStartY = rect.top
  window.addEventListener('mousemove', drag)
  window.addEventListener('mouseup', stopDrag)
  window.addEventListener('touchmove', drag)
  window.addEventListener('touchend', stopDrag)
}

function drag(event: MouseEvent | TouchEvent) {
  if (isDragging) {
    const dx = getEventClientX(event) - dragStartX
    const dy = getEventClientY(event) - dragStartY
    const newLeft = `${modalStartX + dx}px`
    const newTop = `${modalStartY + dy}px`
    const newWidth = modal.value!.offsetWidth + 'px'
    const newHeight = modal.value!.offsetHeight + 'px'
    modal.value!.style.left = newLeft
    modal.value!.style.top = newTop
    globalStore.setModalState(props.modalId, {
      left: newLeft,
      top: newTop,
      width: newWidth,
      height: newHeight
    })
  }
}
function stopDrag() {
  isDragging = false
  window.removeEventListener('mousemove', drag)
  window.removeEventListener('mouseup', stopDrag)
  window.removeEventListener('touchmove', drag)
  window.removeEventListener('touchend', stopDrag)
}

function startResize(event: MouseEvent | TouchEvent) {
  isResizing = true
  modalStartWidth = modal.value!.offsetWidth
  modalStartHeight = modal.value!.offsetHeight
  dragStartX = getEventClientX(event)
  dragStartY = getEventClientY(event)
  window.addEventListener('mousemove', resize)
  window.addEventListener('mouseup', stopResize)
  window.addEventListener('touchmove', resize)
  window.addEventListener('touchend', stopResize)
}

function resize(event: MouseEvent | TouchEvent) {
  if (isResizing) {
    const dx = getEventClientX(event) - dragStartX
    const dy = getEventClientY(event) - dragStartY
    const newWidth = `${modalStartWidth + dx}px`
    const newHeight = `${modalStartHeight + dy}px`
    modal.value!.style.width = newWidth
    modal.value!.style.height = newHeight
    globalStore.setModalState(props.modalId, { width: newWidth, height: newHeight })
  }
}

function stopResize() {
  isResizing = false
  if (modal.value) {
    const rect = modal.value.getBoundingClientRect()
    globalStore.setModalState(props.modalId, {
      left: `${rect.left}px`,
      top: `${rect.top}px`,
      width: `${rect.width}px`,
      height: `${rect.height}px`
    })
  }
  window.removeEventListener('mousemove', resize)
  window.removeEventListener('mouseup', stopResize)
  window.removeEventListener('touchmove', resize)
  window.removeEventListener('touchend', stopResize)
}
// Corrige la posición si la barra superior queda fuera de la pantalla
function ensureModalInViewport() {
  if (!modal.value) return
  const rect = modal.value.getBoundingClientRect()
  let left = rect.left
  let top = rect.top
  let changed = false

  // Limita para que la barra superior no salga por arriba ni por la izquierda
  if (left < 0) {
    left = 0
    changed = true
  }
  if (top < 0) {
    top = 0
    changed = true
  }
  // Opcional: también puedes limitar derecha/abajo si quieres
  const maxLeft = window.innerWidth - rect.width
  const maxTop = window.innerHeight - 40 // 40px: altura aprox. del header
  if (left > maxLeft) {
    left = maxLeft
    changed = true
  }
  if (top > maxTop) {
    top = maxTop
    changed = true
  }

  if (changed) {
    modal.value.style.left = `${left}px`
    modal.value.style.top = `${top}px`
    globalStore.setModalState(props.modalId, {
      left: `${left}px`,
      top: `${top}px`,
      width: rect.width + 'px',
      height: rect.height + 'px'
    })
  }
}


// Corrige al montar y al cambiar tamaño de ventana
onMounted(() => {
  ensureModalInViewport()
  window.addEventListener('resize', ensureModalInViewport)


})

onBeforeUnmount(() => {
  // Guarda el estado actual antes de desmontar
  if (modal.value) {
    const rect = modal.value.getBoundingClientRect()
    globalStore.setModalState(props.modalId, {
      left: `${rect.left}px`,
      top: `${rect.top}px`,
      width: `${rect.width}px`,
      height: `${rect.height}px`
    })
  }
  window.removeEventListener('resize', ensureModalInViewport)

})

// Corrige si cambia el estado del modal (por ejemplo, al abrir/cerrar)
watchEffect(() => {
  if (props.isVisible) {
    setTimeout(ensureModalInViewport, 0)
  }
})
</script>
<style lang="css" scoped>
.modal-container {
  position: fixed;
  background: rgba(50, 50, 50, 0.8);
  border-radius: 5px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  overflow: hidden;
  resize: both;
  color: white;
  transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out;
}

.modal-container.modal-minimized {
  transform: translateX(-100%);
  opacity: 0;
  pointer-events: none;
  /* Quita visibility: hidden */
}

.modal-restore-button {
  position: fixed;
  left: 0;
  width: 200px;
  height: 40px;
  background: rgba(70, 70, 70, 0.9);
  color: white;
  border: 1px solid white;
  border-radius: 0 5px 5px 0;
  cursor: pointer;
  z-index: 999;
  display: flex;
  align-items: center;
  padding: 0 10px;
  transition: all 0.3s ease-in-out;
  transform: translateX(-160px);
  /* Parcialmente oculto */
}

.modal-restore-button:hover {
  transform: translateX(0);
  /* Se muestra completamente al hover */
  background: rgba(70, 70, 70, 1);
}

.modal-restore-button i {
  margin-right: 8px;
}

.modal-restore-button span {
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.modal-header {
  background: rgba(70, 70, 70, 0.9);
  /* Fondo gris oscuro */
  padding: 10px;
  cursor: move;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid white;
  /* Línea blanca */
}

.modal-header h5 {
  margin: 0;
  color: white;
  /* Letras blancas */
}

.modal-body {
  padding-left: 5px;
  padding-right: 20px;
  padding-top: 10px;
  padding-bottom: 10px;
  overflow: auto;
  height: calc(100% - 40px);
  color: white;
  /* Letras blancas */
}

.modal-resizer {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 20px;
  /* Más ancho */
  height: 20px;
  /* Más alto */
  background: white;
  opacity: 0.7;
  border-radius: 0 0 8px 0;
  cursor: se-resize;
  z-index: 10;
  /* Opcional: ícono visual */
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
}

button.btn-close {
  background: none;
  border: none;
  color: white;
  /* Botón de cierre blanco */
  font-size: 16px;
  cursor: pointer;
  height: auto;
}

button.btn-hide {
  background: none;
  border: none;
  color: white;
  /* Botón de cierre blanco */
  font-size: 16px;
  cursor: pointer;
  height: auto;
}

button.btn-close:hover {
  color: red;
  /* Cambia a rojo al pasar el mouse */
}

button.btn-hide:hover {
  color: greenyellow;
}

@media (max-width: 576px) {
  .modal-container-small {
    max-width: 90vw;
    min-width: 200px;
    width: 90vw;
    height: 480px;
    min-height: 400px;
  }
  .modal-header button {
        font-size: 2em;
        margin-top: -5px;
    }
  .modal-header {
    height: 3em;
  }
}
</style>
